import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusTrendDataPoint } from "@/types/security";

interface IssueStatusTrendChartProps {
  data: StatusTrendDataPoint[];
  dateRange: "7" | "30" | "90";
  onDateRangeChange: (range: "7" | "30" | "90") => void;
}

const chartConfig = {
  opened: { label: "Opened", color: "hsl(var(--chart-critical))" },
  resolved: { label: "Resolved", color: "#22c55e" },
  inReview: { label: "In Review", color: "#00B0E4" },
} satisfies ChartConfig;

export function IssueStatusTrendChart({ data, dateRange, onDateRangeChange }: IssueStatusTrendChartProps) {
  return (
    <Card className="rounded-[15px] shadow-none">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
        <div>
          <CardTitle className="text-base font-medium">Opened and Resolved Issues</CardTitle>
          <p className="mt-0.5 text-xs text-muted-foreground">Issue movement over the selected period</p>
        </div>
        <Select value={dateRange} onValueChange={(value) => onDateRangeChange(value as "7" | "30" | "90")}>
          <SelectTrigger className="h-8 w-[118px] rounded-full bg-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-[20px] bg-white">
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <div className="mb-2 flex flex-wrap justify-end gap-3 text-[11px] text-muted-foreground">
          {Object.entries(chartConfig).map(([key, item]) => <span key={key} className="flex items-center gap-1.5"><i className="size-2 rounded-full" style={{ background: item.color }} />{item.label}</span>)}
        </div>
        <ChartContainer config={chartConfig} className="h-[260px] w-full aspect-auto">
          <AreaChart accessibilityLayer data={data} margin={{ left: 8, right: 8, top: 8 }}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={28} tickFormatter={(value: string) => value.slice(5)} />
            <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
            <defs>
              <linearGradient id="fillOpened" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="var(--color-opened)" stopOpacity={0.75}/><stop offset="95%" stopColor="var(--color-opened)" stopOpacity={0.05}/></linearGradient>
              <linearGradient id="fillResolved" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="var(--color-resolved)" stopOpacity={0.65}/><stop offset="95%" stopColor="var(--color-resolved)" stopOpacity={0.05}/></linearGradient>
              <linearGradient id="fillReview" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="var(--color-inReview)" stopOpacity={0.55}/><stop offset="95%" stopColor="var(--color-inReview)" stopOpacity={0.04}/></linearGradient>
            </defs>
            <Area dataKey="opened" type="natural" fill="url(#fillOpened)" fillOpacity={0.4} stroke="var(--color-opened)" strokeWidth={2} />
            <Area dataKey="resolved" type="natural" fill="url(#fillResolved)" fillOpacity={0.35} stroke="var(--color-resolved)" strokeWidth={2} />
            <Area dataKey="inReview" type="natural" fill="url(#fillReview)" fillOpacity={0.3} stroke="var(--color-inReview)" strokeWidth={2} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
